import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { KioskMockup } from "./PillarPonto";
import { ChecklistMockup } from "./PillarChecklists";
import { StockMockup, PhoneScan } from "./PillarEstoque";
import { LiveFeedCard } from "./LiveFeedCard";

// Cole aqui o link do vídeo real (YouTube, Vimeo ou .mp4) para substituir o tour animado.
export const DEMO_VIDEO_URL = "";

const STEP_SECONDS = 15;

const STEPS = [
  { tag: "07:02 · Entrada", title: "O turno começa no totem", desc: "Tablet na parede da cozinha: rosto reconhecido em 2 segundos, sem contato, com PIN de reserva. A folha já começa a ser calculada.", render: () => <KioskMockup /> },
  { tag: "07:10 · Abertura", title: "Checklist com prova real", desc: "Cada item exige foto ao vivo validada por IA e GPS dentro do raio da cozinha. Galeria bloqueada, temperatura conferida contra a faixa.", render: () => <ChecklistMockup /> },
  { tag: "07:20 · Gerência", title: "Gestor avisado no WhatsApp", desc: "Turno concluído, temperatura fora da faixa ou ocorrência crítica: o alerta chega no WhatsApp oficial, com o relatório em PDF.", render: () => <div className="max-w-md mx-auto pt-2 pb-10"><LiveFeedCard /></div> },
  { tag: "09:30 · Manipulação", title: "Etiqueta impressa em 2 segundos", desc: "Etiqueta térmica 50×30 mm com lote, validade em destaque e QR único. Fila PVPS ordenada por quem vence primeiro.", render: () => <StockMockup /> },
  { tag: "11:45 · Cocção", title: "Baixa de estoque pela câmera", desc: "Abriu o pote? Aponte o celular para a etiqueta: baixa registrada em menos de 1 segundo, sem digitar código.", render: () => <div className="flex justify-center py-2"><PhoneScan reduce={false} /></div> },
  { tag: "15:20 · Fechamento", title: "Dia fechado, tudo auditável", desc: "Ponto, checklists e estoque consolidados em um painel só — pronto para a vigilância sanitária, para o RH e para a matriz.", render: () => <ClosingSlide /> },
];

function ClosingSlide() {
  return (
    <div className="rounded-2xl bg-stone-950 text-white p-8 sm:p-10 grid sm:grid-cols-3 gap-6" data-testid="demo-closing-slide">
      {[["100%", "conformidade do turno"], ["0", "papel na cozinha"], ["14 dias", "de teste grátis"]].map(([v, l]) => (
        <div key={l} className="border-l-2 border-emerald-500 pl-4">
          <div className="font-display text-4xl font-black text-emerald-400">{v}</div>
          <div className="font-body text-sm text-stone-400 mt-1">{l}</div>
        </div>
      ))}
      <div className="sm:col-span-3 pt-2">
        <Link to="/register">
          <Button className="bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-body font-semibold h-11 rounded-lg" data-testid="demo-cta-register">
            Começar trial gratuito <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

function VideoEmbed({ url }) {
  const isFile = /\.(mp4|webm|ogg)(\?|$)/i.test(url);
  if (isFile) {
    return <video src={url} controls autoPlay className="w-full aspect-video rounded-xl bg-black" data-testid="demo-video" />;
  }
  const embed = url
    .replace("watch?v=", "embed/")
    .replace("youtu.be/", "www.youtube.com/embed/")
    .replace("vimeo.com/", "player.vimeo.com/video/");
  return (
    <iframe
      src={`${embed}${embed.includes("?") ? "&" : "?"}autoplay=1`}
      title="Ultra em 90 segundos"
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
      className="w-full aspect-video rounded-xl bg-black"
      data-testid="demo-video"
    />
  );
}

function AnimatedTour({ open }) {
  const reduce = useReducedMotion();
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    setStep(0);
    setElapsed(0);
    setPlaying(true);
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open || !playing) return undefined;
    const id = setInterval(() => {
      setElapsed((e) => {
        if (e + 0.1 >= STEP_SECONDS) {
          setStep((s) => {
            if (s >= STEPS.length - 1) {
              setPlaying(false);
              return s;
            }
            return s + 1;
          });
          return 0;
        }
        return e + 0.1;
      });
    }, 100);
    return () => clearInterval(id);
  }, [open, playing]);

  const go = (next) => {
    setStep(Math.max(0, Math.min(STEPS.length - 1, next)));
    setElapsed(0);
  };
  const current = STEPS[step];
  const total = STEPS.length * STEP_SECONDS;
  const position = step * STEP_SECONDS + elapsed;

  return (
    <div data-testid="demo-tour">
      <div className="flex items-center gap-1.5 mb-4">
        {STEPS.map((s, i) => (
          <button
            key={s.title}
            type="button"
            onClick={() => go(i)}
            aria-label={`Ir para etapa ${i + 1}`}
            data-testid={`demo-step-${i}`}
            className="h-1.5 flex-1 rounded-full bg-stone-200 overflow-hidden"
          >
            <div
              className="h-full bg-emerald-600 transition-[width] duration-100 ease-linear"
              style={{ width: i < step ? "100%" : i === step ? `${(elapsed / STEP_SECONDS) * 100}%` : "0%" }}
            />
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.title}
              initial={reduce ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
            >
              <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-800">{current.tag}</div>
              <h3 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-stone-950" data-testid="demo-step-title">
                {current.title}
              </h3>
              <p className="mt-3 font-body text-sm sm:text-base text-stone-600 leading-relaxed">{current.desc}</p>
            </motion.div>
          </AnimatePresence>

          <div className="mt-6 flex items-center gap-2">
            <Button variant="outline" size="icon" className="rounded-full border-stone-300" onClick={() => go(step - 1)} disabled={step === 0} aria-label="Etapa anterior" data-testid="demo-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="icon" className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white" onClick={() => setPlaying((p) => !p)} aria-label={playing ? "Pausar" : "Reproduzir"} data-testid="demo-playpause">
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <Button variant="outline" size="icon" className="rounded-full border-stone-300" onClick={() => go(step + 1)} disabled={step === STEPS.length - 1} aria-label="Próxima etapa" data-testid="demo-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <span className="ml-2 font-mono text-xs text-stone-500" data-testid="demo-timer">
              {Math.floor(position / 60)}:{String(Math.floor(position % 60)).padStart(2, "0")} / {Math.floor(total / 60)}:{String(total % 60).padStart(2, "0")}
            </span>
          </div>
        </div>

        <div className="lg:col-span-8 min-w-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.title}
              initial={reduce ? false : { opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={reduce ? undefined : { opacity: 0, x: -24 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              {current.render()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default function DemoDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[calc(100vw-2rem)] max-h-[92vh] overflow-y-auto p-6 sm:p-8 bg-stone-50 border-stone-200" data-testid="demo-dialog">
        <DialogTitle className="font-display text-lg font-bold tracking-tight text-stone-950 flex items-center gap-2">
          <img src="/logo.png" alt="Ultra" className="h-6 w-6 rounded-md" /> Ultra em 90 segundos
        </DialogTitle>
        <div className="mt-2">{DEMO_VIDEO_URL ? <VideoEmbed url={DEMO_VIDEO_URL} /> : <AnimatedTour open={open} />}</div>
      </DialogContent>
    </Dialog>
  );
}
